import RateReviewIcon from "@mui/icons-material/RateReview"
import { Button, Grid, Paper, Stack, Tooltip, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Box } from "@mui/system"
import { ReactElement } from "react"

import { IChangeRequest } from "../../../services/middleware/types/dnoThreshold/dnoThresholdTypes"

type ChangeRequestMetaProps = {
    changeRequest: IChangeRequest
    showDate?: boolean
    canReview?: boolean
    username?: string
    onReviewClick?: () => void
}

const labelSx = { fontWeight: 600, fontSize: "0.75rem" } as const

/**
 * Displays change request metadata (raised by, reviewed by,
 * comments) in a styled Paper box with two-column layout.
 * Shared between accordion details and review dialog.
 */

const ChangeRequestMeta = ({
    changeRequest: cr,
    showDate = false,
    canReview = false,
    username = "",
    onReviewClick,
}: ChangeRequestMetaProps): ReactElement => (
    <Paper
        variant="outlined"
        sx={(theme) => ({
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor:
                theme.palette.mode === "dark"
                    ? alpha(theme.palette.primary.main, 0.04)
                    : alpha(theme.palette.primary.main, 0.02),
            borderColor:
                theme.palette.mode === "dark"
                    ? alpha(theme.palette.primary.main, 0.15)
                    : theme.palette.divider,
        })}
    >
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
            }}
        >
            <Grid container spacing={2} sx={{ flex: 1 }}>
                <Grid item xs={12} sm={6}>
                    <Stack spacing={0.25}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={labelSx}
                        >
                            Raised by
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                            {cr.raisedByName || "Unknown"}
                        </Typography>
                        {cr.raisedByEmail && (
                            <Typography variant="body2" color="text.secondary">
                                {cr.raisedByEmail}
                            </Typography>
                        )}
                    </Stack>
                    {showDate && (
                        <Stack spacing={0.25} sx={{ mt: 1.5 }}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={labelSx}
                            >
                                Date
                            </Typography>
                            <Typography variant="body2">
                                {new Date(cr.raisedAt).toLocaleDateString(
                                    "en-GB"
                                )}
                            </Typography>
                        </Stack>
                    )}
                    {cr.comment && (
                        <Stack spacing={0.25} sx={{ mt: 1.5 }}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={labelSx}
                            >
                                Submitter Comment
                            </Typography>
                            <Typography variant="body2">
                                {cr.comment}
                            </Typography>
                        </Stack>
                    )}
                </Grid>

                {cr.reviewedBy && (
                    <Grid item xs={12} sm={6}>
                        <Stack spacing={0.25}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={labelSx}
                            >
                                {cr.status === "APPROVED"
                                    ? "Approved by"
                                    : "Rejected by"}
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {cr.reviewedByName || "Unknown"}
                            </Typography>
                            {cr.reviewedByEmail && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {cr.reviewedByEmail}
                                </Typography>
                            )}
                        </Stack>
                        {cr.approvalComment && (
                            <Stack spacing={0.25} sx={{ mt: 1.5 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={labelSx}
                                >
                                    Reviewer Comment
                                </Typography>
                                <Typography variant="body2">
                                    {cr.approvalComment}
                                </Typography>
                            </Stack>
                        )}
                    </Grid>
                )}
            </Grid>

            {canReview && (
                <Tooltip
                    title={
                        cr.raisedBy === username
                            ? "You cannot review your own change request"
                            : ""
                    }
                >
                    <span>
                        <Button
                            variant="outlined"
                            startIcon={<RateReviewIcon />}
                            onClick={onReviewClick}
                            disabled={cr.raisedBy === username}
                            sx={{
                                borderRadius: 6,
                                textTransform: "none",
                                px: 3,
                                py: 1,
                            }}
                        >
                            Review
                        </Button>
                    </span>
                </Tooltip>
            )}
        </Box>
    </Paper>
)

export default ChangeRequestMeta
